// React
import { useState, useId, useMemo } from 'react';

// UI primitives
import { Checkbox } from '@/components/ui/checkbox';
import {
  CommandEmpty,
  CommandSeparator,
} from '@/components/ui/command';
import { DROPDOWN_ROW_HEIGHT, SearchableDropdown } from '@/components/ui/searchable-dropdown';
import { DropdownListItem } from '@/react/components/ui/dropdown-list-item';
import { DropdownTrigger } from '@/react/components/ui/dropdown-trigger';

// Hooks and utilities
import { cn } from '@/lib/utils';
import { useTranslation } from '@hooks/useTranslation';

import { FULL_DAY_TIME_OPTIONS, toTimeLabel, type TimeOption } from './timeOptions';

export const NOW_TIME_VALUE = 'now';

export interface NowTimeOption {
  label: string;
  disabled?: boolean;
  /** Shown under a disabled row to say why. */
  hint?: string;
  /** The trigger's label once "Now" is picked. Default: `label`. */
  selectedLabel?: string;
}

interface ServingTimeDropdownProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  invalid?: boolean;
  /** Override the default 24h × 15-min option list. */
  options?: TimeOption[];
  /** Replace the default 8:00AM–7:30PM list shown until "Show all timings" is ticked. Ignored when `options` is passed. */
  restrictedOptions?: TimeOption[];
  /** Override the default search-input placeholder copy. */
  searchPlaceholder?: string;
  /** Override the default no-results copy. */
  emptyMessage?: string;
  /** When true, re-clicking the already-selected slot deselects it (clears to ''), mirroring the date picker. For optional fields like pre-setup. */
  clearable?: boolean;
  /** When true, a search that is an exact time off the slot grid (11:25, 2:05pm) is offered as an option. */
  allowCustomTime?: boolean;
  /** A "Now" row above the times. */
  nowOption?: NowTimeOption;
  disabled?: boolean;
  /** Popover width as a CSS length. Default: the trigger's width. */
  contentWidth?: string;
  /** Accessible name for the trigger, when no visible label names it. */
  'aria-label'?: string;
}

const RESTRICTED_RANGE_START = '08:00';
const RESTRICTED_RANGE_END = '19:30';
const RESTRICTED_TIME_OPTIONS = FULL_DAY_TIME_OPTIONS.filter(
  ({ value }) => value >= RESTRICTED_RANGE_START && value <= RESTRICTED_RANGE_END,
);

const TIME_QUERY_PATTERN = /^(\d{1,2})(?::?(\d{2}))?(am?|pm?)?$/;
const TIME_VALUE_PATTERN = /^(\d{1,2}):(\d{2})$/;

interface ParsedTimeQuery {
  hour: number;
  minutes?: number;
  period?: 'am' | 'pm';
}

function parseTimeQuery(normalizedQuery: string): ParsedTimeQuery | null {
  const match = TIME_QUERY_PATTERN.exec(normalizedQuery);
  if (!match) return null;

  const [, rawHour, rawMinutes, rawPeriod] = match;
  const hour = Number(rawHour);
  if (hour < 1 || hour > 12) return null;

  const minutes = rawMinutes === undefined ? undefined : Number(rawMinutes);
  if (minutes !== undefined && minutes > 59) return null;

  const period = rawPeriod === undefined ? undefined : rawPeriod.startsWith('a') ? 'am' : 'pm';
  return { hour, minutes, period };
}

const EXACT_TIME_PATTERN = /^(\d{1,2}):?(\d{2})(am?|pm?)?$/;

const toTimeValue = (hour: number, minutes: number) =>
  `${String(hour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

// The exact minutes of the day a search names, as "HH:mm", morning first.
// "2:25pm" and "14:25" name one; "3:34" or "1125" without a period could be
// either half of the day, so both are offered. Empty for anything partial.
export function parseExactTimes(normalizedQuery: string): string[] {
  const match = EXACT_TIME_PATTERN.exec(normalizedQuery);
  if (!match) return [];

  const [, rawHour, rawMinutes, rawPeriod] = match;
  const hour = Number(rawHour);
  const minutes = Number(rawMinutes);
  if (minutes > 59) return [];
  if (rawPeriod) {
    if (hour < 1 || hour > 12) return [];
    return [toTimeValue((hour % 12) + (rawPeriod.startsWith('p') ? 12 : 0), minutes)];
  }
  if (hour >= 1 && hour <= 12) return [toTimeValue(hour % 12, minutes), toTimeValue((hour % 12) + 12, minutes)];
  return hour <= 23 ? [toTimeValue(hour, minutes)] : [];
}

function toTwelveHourParts(value: string) {
  const match = TIME_VALUE_PATTERN.exec(value);
  if (!match) return null;

  const hour24 = Number(match[1]);
  const minutes = Number(match[2]);
  if (hour24 > 23 || minutes > 59) return null;

  return {
    hour: hour24 % 12 === 0 ? 12 : hour24 % 12,
    minutes,
    period: hour24 < 12 ? 'am' : 'pm',
  };
}

function matchesParsedTimeQuery(query: ParsedTimeQuery, option: TimeOption): boolean {
  const parts = toTwelveHourParts(option.value);
  if (!parts) return false;
  if (parts.hour !== query.hour) return false;
  if (query.minutes !== undefined && parts.minutes !== query.minutes) return false;
  if (query.period !== undefined && parts.period !== query.period) return false;
  return true;
}

export function ServingTimeDropdown({
  value,
  onChange,
  placeholder,
  invalid = false,
  options,
  restrictedOptions = RESTRICTED_TIME_OPTIONS,
  searchPlaceholder,
  emptyMessage,
  clearable = false,
  allowCustomTime = false,
  nowOption,
  disabled = false,
  contentWidth,
  'aria-label': ariaLabel,
}: ServingTimeDropdownProps) {
  const { t } = useTranslation();
  const showAllTimingsId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const hasCustomOptions = options !== undefined;
  const isNowSelected = nowOption !== undefined && value === NOW_TIME_VALUE;
  const isValueOutsideRestrictedRange =
    !hasCustomOptions && value !== '' && !isNowSelected && !restrictedOptions.some((option) => option.value === value);
  const [showAllTimings, setShowAllTimings] = useState(isValueOutsideRestrictedRange);

  // The full day keeps any restricted time off the quarter hour, so expanding
  // or searching never drops a time the short list offered.
  const fullDayOptions = useMemo(() => {
    const offGrid = restrictedOptions.filter(
      (option) => !FULL_DAY_TIME_OPTIONS.some((slot) => slot.value === option.value),
    );
    if (offGrid.length === 0) return FULL_DAY_TIME_OPTIONS;
    return [...FULL_DAY_TIME_OPTIONS, ...offGrid].sort((a, b) => a.value.localeCompare(b.value));
  }, [restrictedOptions]);
  const timeOptions = options ?? (showAllTimings ? fullDayOptions : restrictedOptions);
  // Labels and search reach past the restricted range, so a saved or searched
  // time outside it still resolves.
  const allTimeOptions = options ?? fullDayOptions;
  const timeOptionsMap = useMemo(
    () => new Map(allTimeOptions.map((slot) => [slot.value, slot])),
    [allTimeOptions],
  );

  const selectedTimeSlot =
    timeOptionsMap.get(value) ?? (allowCustomTime && value ? { value, label: toTimeLabel(value) } : undefined);
  const selectedLabel = isNowSelected ? (nowOption.selectedLabel ?? nowOption.label) : selectedTimeSlot?.label;

  const filteredTimeSlots = useMemo(() => {
    if (!searchQuery) return timeOptions;
    const normalizedQuery = searchQuery.toLowerCase().replace(/\s/g, '');
    const parsedQuery = parseTimeQuery(normalizedQuery);
    const matches = allTimeOptions.filter(
      (timeSlot) =>
        timeSlot.label.toLowerCase().replace(/\s/g, '').includes(normalizedQuery) ||
        (parsedQuery !== null && matchesParsedTimeQuery(parsedQuery, timeSlot)),
    );
    const custom = allowCustomTime
      ? parseExactTimes(normalizedQuery)
          .filter((exact) => !matches.some((timeSlot) => timeSlot.value === exact))
          .map((exact) => ({ value: exact, label: toTimeLabel(exact) }))
      : [];
    // Matches outside the shown list go last, so the auto-selected first row is one the list was showing.
    const shownValues = new Set(timeOptions.map((timeSlot) => timeSlot.value));
    const isHidden = (timeSlot: TimeOption) => matches.includes(timeSlot) && !shownValues.has(timeSlot.value);
    return [...custom, ...matches].sort(
      (a, b) => Number(isHidden(a)) - Number(isHidden(b)) || a.value.localeCompare(b.value),
    );
  }, [searchQuery, timeOptions, allTimeOptions, allowCustomTime]);

  const shownNow =
    nowOption && (!searchQuery || nowOption.label.toLowerCase().includes(searchQuery.toLowerCase().trim()))
      ? nowOption
      : undefined;

  const select = (next: string) => {
    // When clearable (optional fields like pre-setup), re-selecting
    // the already-chosen slot deselects it — mirroring the date
    // picker's click-again-to-clear. Required fields just re-set.
    onChange(clearable && next === value ? '' : next);
    setIsOpen(false);
  };

  const handleOpenChange = (nextIsOpen: boolean) => {
    if (nextIsOpen && disabled) return;
    if (nextIsOpen) setShowAllTimings(isValueOutsideRestrictedRange);
    setIsOpen(nextIsOpen);
  };

  return (
    <SearchableDropdown
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      selectedValue={value}
      searchPlaceholder={searchPlaceholder ?? t('create_event.search_timing')}
      onSearchChange={setSearchQuery}
      shouldFilter={false}
      contentWidth={contentWidth}
    >
      <SearchableDropdown.Trigger>
        <DropdownTrigger
          selectedLabel={selectedLabel}
          placeholder={placeholder ?? t('create_event.select_timing')}
          isOpen={isOpen}
          invalid={invalid}
          aria-label={ariaLabel}
          disabled={disabled}
        />
      </SearchableDropdown.Trigger>

      <SearchableDropdown.List showFadeGradient maxHeight="320px" className="pl-1.5 py-1.5">
        <CommandEmpty className="p-3 text-left font-inter text-sm text-grey">
          {emptyMessage ?? t('create_event.no_timing_found')}
        </CommandEmpty>

        {shownNow && (
          <DropdownListItem
            value={NOW_TIME_VALUE}
            onSelect={() => select(NOW_TIME_VALUE)}
            isSelected={isNowSelected}
            disabled={shownNow.disabled}
            primary={shownNow.label}
            primaryTitle={shownNow.label}
            secondary={shownNow.disabled ? shownNow.hint : undefined}
            secondaryTitle={shownNow.disabled ? shownNow.hint : undefined}
          />
        )}

        {filteredTimeSlots.map((timeSlot) => {
          const isTimeSelected = timeSlot.value === value;
          return (
            <DropdownListItem
              key={timeSlot.value}
              value={timeSlot.value}
              onSelect={() => select(timeSlot.value)}
              isSelected={isTimeSelected}
              primary={timeSlot.label}
              primaryTitle={timeSlot.label}
            />
          );
        })}
      </SearchableDropdown.List>

      {!hasCustomOptions && (
        <SearchableDropdown.Footer>
          <div>
            {/* Keep the separator visible while cmdk filters the list. */}
            <CommandSeparator alwaysRender className="bg-dinnerplate-white" />
            {/* Keep focus on the search input when toggling the checkbox. */}
            <div
              onMouseDown={(event) => event.preventDefault()}
              className={cn(
                'flex items-center gap-4 rounded-b-input bg-off-white px-6',
                DROPDOWN_ROW_HEIGHT,
              )}
            >
              <Checkbox
                id={showAllTimingsId}
                checked={showAllTimings}
                onCheckedChange={(checked) => setShowAllTimings(checked === true)}
              />
              <label
                htmlFor={showAllTimingsId}
                className="cursor-pointer font-inter text-sm text-grey"
              >
                {t('create_event.show_all_timings')}
              </label>
            </div>
          </div>
        </SearchableDropdown.Footer>
      )}
    </SearchableDropdown>
  );
}
